import React from 'react';
import { Modal, DropdownButton, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { parse, stringify } from 'qs';
import SparkMD5 from 'spark-md5';
import marked from 'marked';
import sanitizeHtml from 'sanitize-html';
import Prism from 'prismjs';
import TimeAgo from 'react-timeago';
import classNames from 'classnames';
import algoliasearch from 'algoliasearch';
import _debounce from 'lodash.debounce';

import ImgWithFallback from '../_global/ImgWithFallback.jsx';
import UserModerationForm from './UserModerationForm.jsx';
import { Setup } from './Setup.jsx';
import Import from './Import.jsx';
import Export from './Export.jsx';
import api from '../../utils/api';

import '../../styles/projects/Comments.css';

const COMMENT_STATUS_APPROVED = 1;
const COMMENT_STATUS_PENDING = 2;

function Divider(props) {

    return (
        <span className={classNames('divider', props.className)}>·</span>
    );
}

function FakeOverlay(props) {

    return (<div>{props.children}</div>);
}

const personalTooltip = (
    <Tooltip id="payment-tooltip">
        Only available with the Moderation Plus Add-on
    </Tooltip>
);

export class Comments extends React.Component {

    static defaultProps = {
        filters: [
            ''
        ]
    };

    mounted = false;

    algoliaCommentsIndex = null;

    state = {
        search: '',
        comments: null,
        loadMore: null,
        info: null,
        filters: [],
        selectedComments: [],
        loading: true,
        selectAll: false,
        blockModal: null,
        userComments: null
    };

    debounceSearch = _debounce(() => {
        this.loadComments();
    }, 250);

    componentDidMount() {

        this.mounted = true;

        this.loadComments();

        const titles = [
            {
                id: 'pending',
                title: 'Pending'
            },
            {
                id: 'flagged',
                title: 'Flagged'
            },
            {
                id: 'approved',
                title: 'Approved'
            }
        ];

        if (this.props.project.userId === this.props.user.uid) {

            titles.push({
                id: 'import',
                title: 'Import'
            });

            titles.push({
                id: 'export',
                title: 'Export'
            });
        }

        if (!titles.find(({ id }) => id === this.props.subcategory)) {
            this.props.onChangeSubcategory(titles[0].id);
        }

        this.props.onChangeSubcategoryTitles(titles);
    }

    componentWillUnmount() {

        this.mounted = false;
    }

    setStateIfMounted(state) {

        if (this.mounted) {
            this.setState(state);
        }
    }

    loadFromDb = cursor => {

        const {
            match: {
                params: { id: projectId }
            }
        } = this.props;

        const { subcategory }  = this.props;
        const status = subcategory === 'pending' ? COMMENT_STATUS_PENDING : COMMENT_STATUS_APPROVED;
        const flagged = status === COMMENT_STATUS_APPROVED && subcategory === 'flagged';

        return api.getProjectComments({ projectId, status, flagged, cursor })
            .then(({ data: { comments, info } }) => {

                this.setStateIfMounted({
                    comments: (this.state.comments || []).concat(comments),
                    loadMore: info.moreResults === 'NO_MORE_RESULTS' ? null : () => this.loadFromDb(info.endCursor)
                });
            });
    };

    loadFromSearch = page => {

        if (!this.algoliaCommentsIndex) {

            const algolia = algoliasearch(process.env.REACT_APP_ALGOLIA_ID, this.props.project.searchApiKey);
            this.algoliaCommentsIndex = algolia.initIndex('comments');
        }

        const status = this.props.subcategory === 'pending' ? COMMENT_STATUS_PENDING : COMMENT_STATUS_APPROVED;
        const flagged = status === COMMENT_STATUS_APPROVED && this.props.subcategory === 'flagged';

        return this.algoliaCommentsIndex.search({
            query: this.state.search,
            hitsPerPage: 50,
            getRankingInfo: true,
            filters: this.state.filters.map(filter => {
                switch(filter) {
                    case 'with-links':
                        return 'links=1';
                    case 'blocked-authors':
                        return 'previouslyBlockedByAuthor=1';
                    case 'previously-removed':
                        return 'previouslyRemovedComment=1';
                    case 'anonymous':
                        return 'anonymous=1';
                    case 'previous-spam':
                        return 'previousSpam=1';
                    default:
                        return '';
                }
            }).concat([`status=${status}`, flagged ? 'flagged=1' : '']).filter(f => !!f).join(' AND '),
            page
        }).then(({ hits, page, nbPages }) => {

            this.setStateIfMounted({
                comments: (this.state.comments || []).concat(hits.map(hit => {

                    hit.name = `${hit.projectId}/${hit.pageId}/${hit.commentId}`;

                    return hit;
                })),
                loadMore: ((page + 1 === nbPages) || !nbPages) ? null : () => this.loadFromSearch(page + 1)
            });
        });
    };

    loadComments = () => {

        this.setState({ loading: true }, () => {

            const useDb = !this.state.search && this.state.filters.length === 0;

            const load = this.state.loadMore || (useDb ? this.loadFromDb : this.loadFromSearch);

            load().catch(err => console.log(err)).then(() => {

                this.setStateIfMounted({ loading: false });
            });
        });
    };

    onToggleSelectAll = ({ currentTarget: { checked } }) => {

        const selectedComments = checked ? (this.state.comments || []).map(c => c.name) : [];

        this.setState({ selectAll: !this.state.selectAll, selectedComments });
    };

    onChangeFilter = ({ currentTarget: { value: filter, checked } }) => {

        let filters = [].concat(this.state.filters);

        if (checked) {
            if (filters.indexOf(filter) === -1) {
                filters.push(filter);
            }
        } else {
           filters = filters.filter(f => f !== filter);
        }

        this.setState({
            filters,
            comments: null,
            loadMore: null,
            selectedComments: [],
        }, this.loadComments);
    };

    onChangeSelectedComments = ({ currentTarget: { value: name, checked } }) => {

        let selectedComments = [].concat(this.state.selectedComments);

        if (checked) {
            if (selectedComments.indexOf(name) === -1) {
                selectedComments.push(name);
            }
        } else {
            selectedComments = selectedComments.filter(n => n !== name);
        }

        this.setState({ selectedComments });
    };

    onClickRemoveComments = e => {

        this.props.changeCover(true, () => {

            this.apiCall = api.removeComments({
                projectId: this.props.project.id,
                comments: this.state.selectedComments.map(n => {

                    const { name, version } = this.state.comments.find(({ name }) => n === name);

                    return { name, version };
                })
            })
                .then(({ data: { saved } }) => {

                    if (this.algoliaCommentsIndex) {
                        this.algoliaCommentsIndex.clearCache();
                    }

                    this.setState(state => {

                        return {
                            comments: (state.comments || []).filter(comment => {

                                return saved.indexOf(comment.name) === -1;
                            }),
                            selectedComments: state.selectedComments.filter(name => {

                                return saved.indexOf(name) === -1;
                            })
                        };
                    }, () => {

                        this.props.changeCover(false);
                    });
                })
                .catch(err => {

                    this.props.changeCover(false);
                });

        });
    };

    onClickApproveComments = e => {

        this.props.changeCover(true, () => {

            this.apiCall = api.approveComments({
                projectId: this.props.project.id,
                comments: this.state.selectedComments.map(n => {

                    const { name, version } = this.state.comments.find(({ name }) => n === name);

                    return { name, version };
                })
            })
                .then(({ data: { saved } }) => {

                    if (this.algoliaCommentsIndex) {
                        this.algoliaCommentsIndex.clearCache();
                    }

                    this.setState(state => {

                        return {
                            comments: (state.comments || []).filter(comment => {

                                return saved.indexOf(comment.name) === -1;
                            }),
                            selectedComments: state.selectedComments.filter(name => {

                                return saved.indexOf(name) === -1;
                            })
                        };

                    }, () => {

                        this.props.changeCover(false);
                    });
                })
                .catch(err => {

                    this.props.changeCover(false);
                });

        });
    };

    onClickClearFlaggedComments = e => {

        this.props.changeCover(true, () => {

            this.apiCall = api.clearFlaggedComments({
                projectId: this.props.project.id,
                comments: this.state.selectedComments.map(n => {

                    const { name, version } = this.state.comments.find(({ name }) => n === name);

                    return { name, version };
                })
            })
                .then(({ data: { saved } }) => {

                    if (this.algoliaCommentsIndex) {
                        this.algoliaCommentsIndex.clearCache();
                    }

                    this.setState(state => {

                        return {
                            comments: (state.comments || []).filter(comment => {

                                return saved.indexOf(comment.name) === -1;
                            }),
                            selectedComments: state.selectedComments.filter(name => {

                                return saved.indexOf(name) === -1;
                            })
                        };

                    }, () => {

                        this.props.changeCover(false);
                    });
                })
                .catch(err => {

                    this.props.changeCover(false);
                });
        });
    };

    onChangeSearch = ({ currentTarget: { value: search } }) => {

        this.setState({
            search,
            comments: null,
            loadMore: null,
            selectedComments: []
        }, this.debounceSearch);
    };

    onClickLoadMore = e => {

        this.loadComments();
    };

    onClickBlockUser = ({ currentTarget: { value: name } }) => {

        this.setState({ blockModal: name });
    };

    onClearBlockUser = e => {

        this.setState({ blockModal: null });
    };

    onToggleUserComments = ({ currentTarget: { value: name } }) => {

        this.setState({ userComments:  this.state.userComments ? null : name});
    };

    get comments() {

        const { renderBody, renderAuthor, renderTimestamp } = this.constructor;
        const { comments } = this.state;
        const hidden = this.props.subcategory === 'pending';


        if (!comments) {
            return null;
        }


        return comments.map(comment => {

            const historyIsOpen = this.state.userComments && (this.state.userComments === comment.name);
            const url = document.createElement('a');
            url.href = comment.url;
            const params = parse(url.search.replace('?', ''));
            params[comment.tlcParam] = SparkMD5.hash(comment.id);
            url.search = `?${stringify(params)}`;

            return (
                <li
                    className={classNames({selected: this.state.selectedComments.indexOf(comment.name) !== -1})}
                    key={comment.id}
                >

                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="media">
                                <div className="media-left">
                                    <ImgWithFallback className="media-object" src={comment.userPhotoURL} />
                                </div>
                                <div className="media-body">
                                    <div className="selector">
                                        <input
                                            type="checkbox"
                                            value={comment.name}
                                            checked={this.state.selectedComments.indexOf(comment.name) !== -1}
                                            onChange={this.onChangeSelectedComments}
                                            disabled={!!this.props.userId}
                                        />
                                    </div>
                                    <div className="body">
                                        {renderBody(comment.body)}
                                    </div>
                                    <div className="footer">
                                        {renderAuthor(comment.userId, comment.userDisplayName)}
                                        <Divider />
                                        {renderTimestamp(comment.updatedAt)}
                                        <Divider />
                                        <button type="button" value={comment.name} onClick={this.onClickBlockUser}>
                                            block user <span className="glyphicon glyphicon-ban-circle" />
                                        </button>
                                        <Divider />
                                        <button
                                            type="button"
                                            value={comment.name}
                                            disabled={!comment.userId}
                                            onClick={this.onToggleUserComments}
                                            className={classNames({ selected: historyIsOpen })}
                                        >
                                            {historyIsOpen ? 'hide' : 'show'} history <span className={`glyphicon glyphicon-arrow-${historyIsOpen ? 'up' : 'down'}`} />
                                        </button>
                                        <Divider className={hidden ? 'hidden' : null} />
                                        <a className={hidden ? 'hidden' : null} target="_blank" href={url.href}>
                                            go to comment <span className="glyphicon glyphicon-share-alt" />
                                        </a>
                                    </div>
                                    {historyIsOpen ? (
                                        <div className="user-comments">
                                            <hr />
                                            <UserComments
                                                {...this.props}
                                                userId={comment.userId}
                                            />
                                            <div className="footer user-footer">
                                                <button
                                                    type="button"
                                                    value={comment.name}
                                                    onClick={this.onToggleUserComments}
                                                    className="selected"
                                                >
                                                    hide history <span className="glyphicon glyphicon-arrow-up" />
                                                </button>
                                            </div>
                                        </div>
                                    ): null}
                                </div>
                            </div>
                        </div>
                    </div>
                </li>
            );
        });
    }

    get blockModal() {

        if (!this.state.blockModal) {
            return null;
        }

        return (
            <UserModerationForm
                changeCover={this.props.changeCover}
                commentName={this.state.blockModal}
                projectId={this.props.project.id}
                isModerationPlus={!!this.props.project.moderationPlusId}
                onHide={this.onClearBlockUser}
            />
        );
    }

    get filters() {

        const personal = !this.props.project.searchApiKey;
        const Overlay = personal ? OverlayTrigger : FakeOverlay;

        return Setup.defaultProps.approvalProcessGranularOptions.map(({ value, desc }) => (
            <li role="presentation" key={value}>
                <Overlay placement="top" overlay={personalTooltip}>
                    <div className={classNames('checkbox', { disabled: !!personal })}>
                        <label role="menuitem">
                            <input
                                type="checkbox"
                                value={value}
                                checked={this.state.filters.indexOf(value) !== -1}
                                onChange={this.onChangeFilter}
                                disabled={personal}
                            />
                            Comments {desc}
                        </label>
                    </div>
                </Overlay>
            </li>
        ));
    }

    get actions() {

        const actions = [
            <button
                key="remove"
                className="btn btn-danger"
                onClick={this.onClickRemoveComments}
                type="button"
            >
                Remove ({this.state.selectedComments.length})
            </button>
        ];

        if (this.props.subcategory === 'pending') {

            actions.push(
                <button
                    key="approve"
                    className="btn btn-info"
                    onClick={this.onClickApproveComments}
                    type="button"
                >
                    Approve ({this.state.selectedComments.length})
                </button>
            );
        }

        if (this.props.subcategory === 'flagged') {

            actions.push(
                <button
                    key="clear-flagged"
                    className="btn btn-info"
                    onClick={this.onClickClearFlaggedComments}
                    type="button"
                >
                    Approve ({this.state.selectedComments.length})
                </button>
            );
        }

        return actions;
    }

    get toolbar() {

        const personal = !this.props.project.searchApiKey;
        const Overlay = personal ? OverlayTrigger : FakeOverlay;

        return (
            <div className="row">
                <div className="col-lg-8 col-md-8 col-sm-12">

                    <form>

                        <div className={classNames('form-group searchbar', { active: !!this.state.search })}>
                            <Overlay placement="top" overlay={personalTooltip}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={'Enter search text here'}
                                    disabled={personal}
                                    value={this.state.search}
                                    onChange={this.onChangeSearch}
                                />
                            </Overlay>
                            <button type="button" value="" onClick={this.onChangeSearch}>x</button>
                        </div>

                    </form>

                </div>

                <div className="col-lg-3 col-md-3 col-sm-10 col-xs-10">

                    <div className="filters">

                        <div className="btn-group btn-group-justified">

                            <DropdownButton
                                bsStyle="default"
                                id="filters"
                                title={`Filters (${this.state.filters.length})`}
                            >
                                {this.filters}
                            </DropdownButton>

                        </div>

                    </div>

                </div>

                <div className="col-lg-1 col-md-1 col-sm-2 col-xs-2">
                    <div className="select-all">
                        <label>
                            <input
                                type="checkbox"
                                checked={this.state.selectAll}
                                onChange={this.onToggleSelectAll}
                            />
                        </label>
                    </div>
                </div>

            </div>
        );
    }

    render() {

        if (this.props.subcategory === 'export') {

            return (
                <Export projectId={this.props.project.id} />
            );
        }

        if (this.props.subcategory === 'import') {
            return (
                <Import
                    userId={this.props.user.uid}
                    projectId={this.props.project.id}
                    domain={this.props.project.domain}
                />
            );
        }

        const { comments, loadMore } = this.state;
        let buttonText = 'Loading...';

        if (comments && loadMore) {
            buttonText = 'Load more';
        }

        if (comments && !loadMore) {

            const kind = this.props.userId
                ? 'user'
                : this.props.subcategory === 'pending'
                    ? 'pending'
                    : this.props.subcategory === 'flagged'
                        ? 'flagged'
                        : 'approved';

            buttonText = comments.length === 0 ? `No ${kind} comments` : `No more ${kind} comments`;
        }

        return (
            <div className="comments">

                {this.blockModal}

                <div className="toolbar">

                    {this.toolbar}
                </div>


                <ul className="comment-list">
                    {this.comments}
                </ul>
                <div className="row">
                    <div className="col-lg-6 col-lg-offset-3 col-md-6 col-md-offset-3 col-sm-12">
                        <hr />
                        <button
                            type="button"
                            className="btn btn-success btn-lg btn-block"
                            disabled={!loadMore}
                            onClick={this.onClickLoadMore}
                        >
                            {buttonText}
                        </button>
                    </div>
                </div>

                <div className={classNames('actions', { showing: this.state.selectedComments.length > 0 })}>
                    {this.actions}
                </div>
            </div>
        );
    }

    static renderBody(body) {

        if (!body) {
            return (
                <div>
                    This comment has been deleted.
                </div>
            );
        }

        const html = marked(body, {
            breaks: true
        });
        const cleanHtml = sanitizeHtml(html, {
            allowedTags: [ 'b', 'i', 'em', 'strong', 'br', 'p', 'pre', 'code', 'blockquote' ],
            allowedAttributes: {
                'code': ['class']
            }
        });

        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: cleanHtml
                }}
                ref={div => div ? Prism.highlightAllUnder(div) : null}
            />
        );
    }

    static renderTimestamp(timestamp) {

        return (
            <TimeAgo key="timestamp" date={Number(timestamp)} minPeriod={15} formatter={Comments.timestampFormatter} />
        );
    }

    static timestampFormatter(value, unit, suffix, date) {

        return `${value}${unit.charAt(0)}`;
    }

    static renderAuthor(userId, userDisplayName) {

        if (!userId) {
            userDisplayName = 'Anonymous';
        }

        return `- ${userDisplayName}`;
    }
}

class UserComments extends Comments {

    componentDidMount() {

        this.mounted = true;

        this.loadComments();
    }

    loadFromDb = cursor => {

        const { project, userId } = this.props;

        return api.getUserHistory({ projectId: project.id, userId, cursor })
            .then(({ data: { comments, info } }) => {

                this.setStateIfMounted({
                    comments: (this.state.comments || []).concat(comments),
                    loadMore: info.moreResults === 'NO_MORE_RESULTS' ? null : () => this.loadFromDb(info.endCursor)
                });
            });
    };

    loadFromSearch = page => {

        if (!this.algoliaCommentsIndex) {

            const algolia = algoliasearch(process.env.REACT_APP_ALGOLIA_ID, this.props.project.searchApiKey);
            this.algoliaCommentsIndex = algolia.initIndex('comments');
        }

        return this.algoliaCommentsIndex.search({
            query: this.state.search,
            hitsPerPage: 10,
            getRankingInfo: true,
            filters: this.state.filters.map(filter => {
                switch(filter) {
                    case 'with-links':
                        return 'links=1';
                    case 'blocked-authors':
                        return 'previouslyBlockedByAuthor=1';
                    case 'previously-removed':
                        return 'previouslyRemovedComment=1';
                    case 'anonymous':
                        return 'anonymous=1';
                    case 'previous-spam':
                        return 'previousSpam=1';
                    default:
                        return '';
                }
            }).concat(`userId:${this.props.userId}`).filter(f => !!f).join(' AND '),
            page
        }).then(({ hits, page, nbPages }) => {

            this.setStateIfMounted({
                comments: (this.state.comments || []).concat(hits.map(hit => {

                    hit.name = `${hit.projectId}/${hit.pageId}/${hit.commentId}`;

                    return hit;
                })),
                loadMore: ((page + 1 === nbPages) || !nbPages) ? null : () => this.loadFromSearch(page + 1)
            });
        });
    };

    get actions() {
        return null;
    }

    get comments() {

        const { renderBody, renderAuthor, renderTimestamp } = this.constructor;
        const { comments } = this.state;

        if (!comments) {
            return null;
        }

        return comments.map(comment => (
            <li
                className={classNames({selected: this.state.selectedComments.indexOf(comment.name) !== -1})}
                key={comment.id}
            >
                <div className="panel panel-default">
                    <div className="panel-body">
                        <div className="media">
                            <div className="media-left">
                                <ImgWithFallback className="media-object" src={comment.userPhotoURL} />
                            </div>
                            <div className="media-body">
                                <div className="body">
                                    {renderBody(comment.body)}
                                </div>
                                <div className={classNames('footer', {hidden: !comment.body})}>
                                    {renderAuthor(comment.userId, comment.userDisplayName)}
                                    <Divider />
                                    {renderTimestamp(comment.updatedAt)}
                                    <Divider />
                                    <span className={`label label-${comment.status === 1 ? 'success' : 'warning'}`}>
                                        {comment.status === 1 ? 'approved' : 'pending'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </li>
        ));
    }

    get toolbar() {

        const personal = !this.props.project.searchApiKey;
        const Overlay = personal ? OverlayTrigger : FakeOverlay;

        return (
            <div className="row">
                <div className="col-lg-9 col-md-9 col-sm-12">

                    <form>

                        <div className={classNames('form-group searchbar', { active: !!this.state.search })}>
                            <Overlay placement="top" overlay={personalTooltip}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={'Enter search text here'}
                                    disabled={personal}
                                    value={this.state.search}
                                    onChange={this.onChangeSearch}
                                />
                            </Overlay>
                            <button type="button" value="" onClick={this.onChangeSearch}>x</button>
                        </div>

                    </form>

                </div>

                <div className="col-lg-3 col-md-3 col-sm-12 col-xs-12">

                    <div className="filters">

                        <div className="btn-group btn-group-justified">

                            <DropdownButton
                                bsStyle="default"
                                id="filters"
                                title={`Filters (${this.state.filters.length})`}
                            >
                                {this.filters}
                            </DropdownButton>

                        </div>

                    </div>

                </div>


            </div>
        );
    }
}

export default Comments;


// WEBPACK FOOTER //
// ./src/components/projects/Comments.jsx